import { Routes, Route, Navigate } from 'react-router-dom'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { hydrateFromStorage } from './app/redux/slices/authSlice'
import type { AppDispatch, RootState } from './app/redux/store'

// Route components - we'll import these as we port them
import LoginPage from './pages/LoginPage'
import DashboardPage from './pages/DashboardPage'
import SessionPage from './pages/SessionPage'
import DataDownloadPage from './pages/DataDownloadPage'
import TeamsPage from './pages/TeamsPage'
import TeamDetailsPage from './pages/TeamDetailsPage'
import AccountPage from './pages/AccountPage'
import MoveSmartProbePage from './pages/MoveSmartProbePage'
import RegisterPage from './pages/RegisterPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import VerificationPage from './pages/VerificationPage'
import ContinueVerificationPage from './pages/ContinueVerificationPage'

// Route guards
import RequireAuth from './guards/RequireAuth'
import PublicOnly from './guards/PublicOnly'

function App() {
  const dispatch = useDispatch<AppDispatch>()
  const hydrated = useSelector((state: RootState) => state.auth.hydrated)

  // Hydrate auth from storage on mount
  useEffect(() => {
    if (typeof window !== 'undefined' && !hydrated) {
      dispatch(hydrateFromStorage())
    }
  }, [dispatch, hydrated])

  // Show loading while hydrating
  if (!hydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <Routes>
      {/* Root redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* Public routes (redirect to dashboard if authenticated) */}
      <Route path="/login" element={
        <PublicOnly>
          <LoginPage />
        </PublicOnly>
      } />
      <Route path="/register" element={
        <PublicOnly>
          <RegisterPage />
        </PublicOnly>
      } />
      <Route path="/forgot-password" element={
        <PublicOnly>
          <ForgotPasswordPage />
        </PublicOnly>
      } />
      <Route path="/reset-password" element={
        <PublicOnly>
          <ResetPasswordPage />
        </PublicOnly>
      } />
      <Route path="/verification" element={
        <PublicOnly>
          <VerificationPage />
        </PublicOnly>
      } />
      <Route path="/continue-verification" element={
        <PublicOnly>
          <ContinueVerificationPage />
        </PublicOnly>
      } />

      {/* Protected routes (require authentication) */}
      <Route path="/dashboard" element={
        <RequireAuth>
          <DashboardPage />
        </RequireAuth>
      } />
      <Route path="/session" element={
        <RequireAuth>
          <SessionPage />
        </RequireAuth>
      } />
      <Route path="/data-download" element={
        <RequireAuth>
          <DataDownloadPage />
        </RequireAuth>
      } />
      <Route path="/teams" element={
        <RequireAuth>
          <TeamsPage />
        </RequireAuth>
      } />
      <Route path="/teams/details/:id" element={
        <RequireAuth>
          <TeamDetailsPage />
        </RequireAuth>
      } />
      <Route path="/account" element={
        <RequireAuth>
          <AccountPage />
        </RequireAuth>
      } />
      <Route path="/move-smartprobe" element={
        <RequireAuth>
          <MoveSmartProbePage />
        </RequireAuth>
      } />

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  )
}

export default App

